import React, { useEffect, useRef, useState } from 'react'
import { Button, Form, FormInstance, Switch, Upload } from 'antd'
import { DeleteOutlined, UploadOutlined } from '@ant-design/icons'
import SelectForm from 'components/SelectForm/SelectForm'
import InputPrefix from 'components/InputPrefix/InputPrefix'
import { RecursoMetadata, TIPOS_RECURSO } from '../interfaces/ConfiguracionRecursos.interfaces'
import { FIELDS_NAMES_RECURSO } from '../interfaces/FieldNamesRecurso'
import {
  buildBeforeUpload,
  construirUrlRecurso,
  extraerMetadataArchivo,
  formatearPeso,
} from '../helpers/ModalRecursos.helper'
import { validarLongitudMax50 } from 'util/funciones'
import useTiposRecurso from '../hooks/useTipoRecurso'

const SIN_ARCHIVOS: File[] = []

interface SelectorArchivosProps {
  /** Inyectados por Form.Item: el valor del campo es la lista de `File`. */
  value?: File[]
  onChange?: (files: File[]) => void
  esVideo: boolean
  readOnly: boolean
  /** En edición un recurso apunta a un solo archivo; en creación se permiten varios. */
  multiple: boolean
  /** URL ya guardada (modo editar/ver), para previsualizar el archivo actual. */
  urlExistente?: string
  onPrimerArchivoSeleccionado: (file: File) => void
}

/**
 * Control de archivos del form: selecciona, previsualiza y expone los `File`.
 * No sube nada al backend; los archivos se envían al confirmar el modal.
 */
const SelectorArchivos = ({
  value,
  onChange,
  esVideo,
  readOnly,
  multiple,
  urlExistente,
  onPrimerArchivoSeleccionado,
}: SelectorArchivosProps) => {
  // Identidad estable cuando el campo aún no tiene valor: los efectos de abajo
  // dependen de `archivos` y un `[]` nuevo en cada render los dispararía en bucle.
  const archivos = value ?? SIN_ARCHIVOS

  const [leyendo, setLeyendo] = useState(false)
  const [previews, setPreviews] = useState<string[]>([])
  const [metadatas, setMetadatas] = useState<RecursoMetadata[]>([])

  // Los `File` viven en el form (sobreviven al cambio de paso del wizard), así
  // que preview y metadata se derivan de ellos y se liberan al cambiar la lista.
  useEffect(() => {
    const objectUrls = archivos.map((archivo) => URL.createObjectURL(archivo))
    setPreviews(objectUrls)
    return () => objectUrls.forEach((objectUrl) => URL.revokeObjectURL(objectUrl))
  }, [archivos])

  useEffect(() => {
    let cancelado = false
    Promise.all(archivos.map(extraerMetadataArchivo)).then((metas) => {
      if (!cancelado) setMetadatas(metas)
    })
    return () => {
      cancelado = true
    }
  }, [archivos])

  // `beforeUpload` se dispara una vez por archivo y en paralelo cuando se
  // seleccionan varios, por eso la lista se acumula sobre la referencia y no
  // sobre el `value` capturado en el render.
  const archivosRef = useRef<File[]>(archivos)
  archivosRef.current = archivos

  const handleBeforeUpload = async (file: File) => {
    const valido = buildBeforeUpload(esVideo ? TIPOS_RECURSO.VIDEO : TIPOS_RECURSO.IMAGEN)(file)
    if (valido !== true) return valido

    setLeyendo(true)
    try {
      const siguientes = multiple ? [...archivosRef.current, file] : [file]
      archivosRef.current = siguientes
      onChange?.(siguientes)
      if (siguientes.length === 1) onPrimerArchivoSeleccionado(file)
    } finally {
      setLeyendo(false)
    }
    // `false` evita que antd suba el archivo por su cuenta.
    return false
  }

  const quitarArchivo = (indice: number) => {
    const siguientes = archivos.filter((_, i) => i !== indice)
    archivosRef.current = siguientes
    onChange?.(siguientes)
  }

  const textoBoton = archivos.length > 0 || urlExistente ? 'Agregar otro archivo' : 'Seleccionar archivo'

  return (
    <>
      <Upload
        accept={esVideo ? '.mp4' : '.png,.webp'}
        beforeUpload={handleBeforeUpload}
        multiple={multiple}
        showUploadList={false}
        fileList={[]}
        disabled={readOnly || leyendo}
      >
        <Button icon={<UploadOutlined />} loading={leyendo} disabled={readOnly}>
          {multiple ? textoBoton : archivos.length > 0 || urlExistente ? 'Cambiar archivo' : 'Seleccionar archivo'}
        </Button>
      </Upload>

      {/* Archivo ya guardado (editar/ver) cuando todavía no se eligió uno nuevo. */}
      {archivos.length === 0 && urlExistente && (
        <div style={contenedorPreview}>
          {esVideo ? (
            <video src={urlExistente} controls style={estiloVideo} />
          ) : (
            <img src={urlExistente} alt="Previsualización del recurso" style={estiloImagen} />
          )}
          <div style={{ fontSize: 12, color: '#595959' }}>Archivo actual del recurso</div>
        </div>
      )}

      {/* La lista muestra 2 previews y el resto queda con scroll, para que el
          modal no crezca al adjuntar varios archivos. */}
      {archivos.length > 0 && (
        <div style={listaPreviews}>
          {archivos.map((archivo, indice) => (
            <div key={`${archivo.name}-${archivo.lastModified}-${indice}`} style={contenedorPreview}>
              {/* El objectURL se crea en un efecto: en el render en que se agrega el
                  archivo todavía no existe. */}
              {previews[indice] &&
                (esVideo ? (
                  <video src={previews[indice]} controls style={estiloVideo} />
                ) : (
                  <img src={previews[indice]} alt={archivo.name} style={estiloImagen} />
                ))}

              <div style={{ fontSize: 12, color: '#595959', overflow: 'hidden', flex: 1 }}>
                <div style={{ fontWeight: 600, wordBreak: 'break-all' }}>{archivo.name}</div>
                {metadatas[indice] && (
                  <div style={{ color: '#8c8c8c' }}>
                    {metadatas[indice].ancho && metadatas[indice].alto
                      ? `${metadatas[indice].ancho} × ${metadatas[indice].alto} px · `
                      : ''}
                    {formatearPeso(metadatas[indice].peso_bytes)}
                    {metadatas[indice].duracion_segundos ? ` · ${metadatas[indice].duracion_segundos} s` : ''}
                  </div>
                )}
              </div>

              {!readOnly && (
                <Button type="text" danger icon={<DeleteOutlined />} onClick={() => quitarArchivo(indice)} />
              )}
            </div>
          ))}
        </div>
      )}

      {archivos.length > 2 && (
        <span style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4, display: 'block' }}>
          {archivos.length} archivos adjuntos · desplázate en la lista para verlos todos.
        </span>
      )}

      <span style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4, display: 'block' }}>
        Solo se permiten archivos {esVideo ? '.mp4' : '.png o .webp'}
        {multiple ? '. Puedes adjuntar varios: se creará un recurso por cada archivo.' : '.'}
      </span>
    </>
  )
}

// Alto de cada fila del preview: media (80) + padding (8 arriba y abajo) +
// borde (1 y 1) + la separación con la fila anterior (8).
const ALTO_FILA_PREVIEW = 106

const listaPreviews: React.CSSProperties = {
  maxHeight: ALTO_FILA_PREVIEW * 2,
  overflowY: 'auto',
  // Deja aire para la barra de scroll y que no tape el botón de eliminar.
  paddingRight: 4,
}

const contenedorPreview: React.CSSProperties = {
  display: 'flex',
  gap: 12,
  alignItems: 'center',
  marginTop: 8,
  padding: 8,
  border: '1px solid #f0f0f0',
  borderRadius: 8,
}

const estiloVideo: React.CSSProperties = {
  width: 140,
  height: 80,
  objectFit: 'cover',
  borderRadius: 6,
  background: '#000',
  flexShrink: 0,
}

const estiloImagen: React.CSSProperties = {
  width: 140,
  height: 80,
  objectFit: 'contain',
  borderRadius: 6,
  background: '#fafafa',
  flexShrink: 0,
}

interface PasoDatosRecursoProps {
  form: FormInstance
  readOnly: boolean
  mostrarEstado?: boolean
  /** Permite adjuntar varios archivos (creación y edición). */
  permitirVariosArchivos?: boolean
  /**
   * En edición el tipo queda bloqueado (el archivo guardado ya es de ese tipo) y
   * los archivos extra se crean como recursos nuevos en la misma estación.
   */
  esEdicion?: boolean
}

/**
 * Paso 2: datos propios del recurso (tipo, nombre, archivos y estado).
 * Todos los campos viven en el `form` compartido del wizard.
 *
 * Los archivos NO se suben aquí: se guardan los `File` en el form y se muestra
 * un preview local. El envío ocurre al confirmar el modal (ver
 * ModalRecurso.handleSubmit), para no dejar archivos huérfanos en el servidor
 * si el usuario cancela.
 *
 * `mostrarEstado`: en modo "crear" no se muestra el switch, el recurso
 * siempre se crea activo (el valor `activo: true` se fija por defecto en
 * el form). En "editar"/"ver" sí se muestra para poder desactivarlo.
 */
const PasoDatosRecurso = ({
  form,
  readOnly,
  mostrarEstado = true,
  permitirVariosArchivos = false,
  esEdicion = false,
}: PasoDatosRecursoProps) => {
  const { options: tiposOptions, loading: loadingTipos } = useTiposRecurso()

  const tipoId: number = Form.useWatch(FIELDS_NAMES_RECURSO.TIPO, form) ?? TIPOS_RECURSO.IMAGEN
  const url: string | undefined = Form.useWatch(FIELDS_NAMES_RECURSO.URL, { form, preserve: true })
  const activo: boolean = Form.useWatch(FIELDS_NAMES_RECURSO.ACTIVO, { form, preserve: true }) ?? true
  const archivos: File[] = Form.useWatch(FIELDS_NAMES_RECURSO.ARCHIVOS, { form, preserve: true }) ?? []

  // Al cambiar de imagen a video (o viceversa) los archivos ya elegidos dejan
  // de ser válidos para el tipo seleccionado.
  const tipoPrevio = useRef(tipoId)
  useEffect(() => {
    if (tipoPrevio.current !== tipoId) {
      tipoPrevio.current = tipoId
      form.setFieldValue(FIELDS_NAMES_RECURSO.ARCHIVOS, [])
    }
  }, [tipoId])

  const handlePrimerArchivo = (file: File) => {
    // Sugerimos el nombre del archivo si el usuario aún no escribió uno.
    if (!form.getFieldValue(FIELDS_NAMES_RECURSO.NOMBRE)) {
      form.setFieldValue(FIELDS_NAMES_RECURSO.NOMBRE, file.name.replace(/\.[^.]+$/, '').slice(0, 50))
    }
  }

  return (
    <>
      <Form.Item
        label="TIPO"
        name={FIELDS_NAMES_RECURSO.TIPO}
        rules={[{ required: true, message: 'Seleccione el tipo de recurso' }]}
        initialValue={TIPOS_RECURSO.IMAGEN}
      >
        {/* En edición el tipo no se cambia: el archivo ya guardado es de ese
            tipo y los que se agreguen deben ser del mismo. */}
        <SelectForm options={tiposOptions} loading={loadingTipos} disabled={readOnly || esEdicion} />
      </Form.Item>

      <InputPrefix
        name={FIELDS_NAMES_RECURSO.NOMBRE}
        rules={[
          { required: true, whitespace: true, message: 'Ingrese el nombre del recurso' },
          { validator: validarLongitudMax50 },
        ]}
        prefix="Nombre*"
        placeholder="Ej. Banner promocional"
        disabled={readOnly}
      />

      {archivos.length > 1 && !esEdicion && (
        <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: -8, marginBottom: 12 }}>
          Se creará un recurso por archivo y el nombre se numerará: «{form.getFieldValue(FIELDS_NAMES_RECURSO.NOMBRE) || 'Nombre'} (1)», «
          {form.getFieldValue(FIELDS_NAMES_RECURSO.NOMBRE) || 'Nombre'} (2)»…
        </div>
      )}

      {esEdicion && (
        <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: -8, marginBottom: 12 }}>
          {archivos.length > 1
            ? `El primer archivo reemplaza el actual (el anterior se elimina) y los otros ${archivos.length - 1} se agregan como recursos nuevos en la misma estación.`
            : 'Si adjuntas un archivo reemplaza el actual y el anterior se elimina. Puedes adjuntar más para agregarlos como recursos nuevos en la misma estación.'}
        </div>
      )}

      <Form.Item
        label={permitirVariosArchivos ? 'ARCHIVOS' : 'ARCHIVO'}
        name={FIELDS_NAMES_RECURSO.ARCHIVOS}
        rules={[
          {
            // `required` no sirve acá: el valor es un arreglo y `[]` lo pasa.
            validator: (_, seleccionados: File[] = []) =>
              seleccionados.length > 0 || url
                ? Promise.resolve()
                : Promise.reject(new Error('Cargue al menos un archivo')),
          },
        ]}
      >
        <SelectorArchivos
          esVideo={tipoId === TIPOS_RECURSO.VIDEO}
          readOnly={readOnly}
          multiple={permitirVariosArchivos}
          // Solo para el preview: en el form se conserva la url tal cual, que es
          // la que se reenvía al editar.
          urlExistente={construirUrlRecurso(url)}
          onPrimerArchivoSeleccionado={handlePrimerArchivo}
        />
      </Form.Item>

      {/* En creación no se muestra: el recurso siempre se crea activo
          (el valor por defecto `activo: true` se fija en ModalRecurso/index.tsx). */}
      {mostrarEstado && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontWeight: 600 }}>Estado</div>
            <div style={{ fontSize: 12, color: '#8c8c8c' }}>Habilita este recurso para su uso.</div>
          </div>
          <Switch
            checked={activo}
            disabled={readOnly}
            onChange={(checked) => form.setFieldValue(FIELDS_NAMES_RECURSO.ACTIVO, checked)}
          />
        </div>
      )}
    </>
  )
}

export default PasoDatosRecurso
